import { templateFoldersKeys } from "@/queries/forms/template-folder.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiFormTemplate,
  ApiFormTemplateDuplicatePayload,
  ApiFormTemplateType,
  ApiFormTemplateFilter,
  ApiFormTemplateMovePayload,
  ApiFormTemplateSavePayload,
} from "@/types/forms/form-template.types.ts";
import { Control, FieldValues } from "react-hook-form";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { FormProviderTypeSaveAsTemplate } from "@/components/forms/form-editor.types.ts";
import { apiPerformanceReviews } from "@/api/performance-review.api.ts";
const performanceReviewTemplateKeys = createQueryKeys("performanceReviewTemplates", {
  byId: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (!id) throw new ApiPassException("Id is required");
      const result = await apiPerformanceReviews.getPerformanceReviewTemplateById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  search: (filter: ApiFormTemplateFilter) => ({ queryKey: [filter] }),
});

export const usePerformanceReviewTemplateById = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...performanceReviewTemplateKeys.byId(id),
    enabled: !!id && enabled,
  });
};

export const usePerformanceReviewTemplateCreateFromReviewerType = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: FormProviderTypeSaveAsTemplate) => {
      const result = await apiPerformanceReviews.createPerformanceReviewTemplateFromReviewerType(
        id,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate) => {
      queryClient.setQueryData(performanceReviewTemplateKeys.byId(template.id).queryKey, template);
      queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewTemplateSearch = (
  filter: ApiFormTemplateFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...performanceReviewTemplateKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiPerformanceReviews.searchPerformanceReviewTemplates(
        { ...filter },
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export const usePerformanceReviewTemplateDuplicate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({
      id,
      payload = {},
    }: {
      id: number;
      payload?: ApiFormTemplateDuplicatePayload;
    }) {
      const result = await apiPerformanceReviews.duplicatePerformanceReviewTemplate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate, { payload }) => {
      if (payload?.target_type === ApiFormTemplateType.survey) {
        queryClient.invalidateQueries({ queryKey: ["surveyTemplates"] });
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("surveys").queryKey,
        });
      } else {
        queryClient.setQueryData(
          performanceReviewTemplateKeys.byId(template.id).queryKey,
          template
        );
        queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys.search._def });
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
        });
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export interface PerformanceReviewTemplateSaveParams {
  id?: number;
  payload: ApiFormTemplateSavePayload;
}

export const usePerformanceReviewTemplateSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: PerformanceReviewTemplateSaveParams) => {
      const result = id
        ? await apiPerformanceReviews.updatePerformanceReviewTemplate(id, payload)
        : await apiPerformanceReviews.createPerformanceReviewTemplate(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate, { id }) => {
      queryClient.setQueryData(performanceReviewTemplateKeys.byId(template.id).queryKey, template);
      queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys.search._def });
      if (!id)
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
        });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewTemplateDelete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiPerformanceReviews.deletePerformanceReviewTemplate(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
      });
      queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys.byId(id).queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const usePerformanceReviewTemplateMove = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiFormTemplateMovePayload }) => {
      const result = await apiPerformanceReviews.movePerformanceReviewTemplate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: performanceReviewTemplateKeys._def }),
        queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
        }),
      ]),
    onError: (error: Error) => onError(error),
  });
};
